import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, GraduationCap, Layers, Sparkles } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  cn,
} from "@eduviaos/ui-kit";
import { useSections, useSubjects } from "@/hooks/useAcademicCatalogue";

/**
 * Phase H4 FE-1 — IA Catalogue landing page.
 *
 * The Catalogue is the new home for tenant-wide academic configuration
 * — subjects, sections, and the subject-coverage matrix that drives
 * the eligibility builder. It exists because the timetable, attendance,
 * substitution, and exam modules all depend on this data being present
 * before they can render anything useful, and a single landing surface
 * makes the "configure these BEFORE you open Timetable" gate obvious.
 *
 * The cards on this page also surface live counts so the IA can spot a
 * gap (e.g. "0 sections in branch X") at a glance before clicking into
 * the detail view.
 */

interface CatalogueCard {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  href: string;
  count: number | null;
  countLabel: string;
}

export function CataloguePage() {
  const subjectsQuery = useSubjects();
  const sectionsQuery = useSections();

  const subjects = subjectsQuery.data?.items ?? [];
  const sections = sectionsQuery.data?.items ?? [];

  const isLoading = subjectsQuery.isLoading || sectionsQuery.isLoading;
  const isReadyMatrix = subjects.length > 0;

  const cards: CatalogueCard[] = [
    {
      icon: BookOpen,
      title: "Subjects",
      description:
        "Institution-wide subject codes, kinds, lab requirements, and per-week period defaults. The timetable solver and eligibility builder both pull from this list.",
      href: "/catalogue/subjects",
      count: subjectsQuery.isLoading ? null : subjects.length,
      countLabel: "subjects",
    },
    {
      icon: Layers,
      title: "Sections",
      description:
        "Per-branch class sections scoped by grade and academic year. Anchors every timetable slot and the attendance roll-call sheet.",
      href: "/catalogue/sections",
      count: sectionsQuery.isLoading ? null : sections.length,
      countLabel: "sections",
    },
    {
      icon: GraduationCap,
      title: "Subject coverage matrix",
      description:
        "Subject × grade applicability with section-availability hints. Use this to plan which staff invites to send before opening the Timetable workspace.",
      href: "/catalogue/eligibility-matrix",
      count: null,
      countLabel: "",
    },
  ];

  return (
    <div className="mx-auto max-w-6xl py-8">
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/dashboard">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Dashboard
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Catalogue</PageHeaderHeading>
            <PageHeaderDescription>
              Institution-wide academic data — subjects, sections, and the coverage matrix.
              Configure this before inviting teachers or opening the Timetable workspace.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <Link
            key={card.title}
            to={card.href}
            className="focus-visible:ring-ring focus-visible:ring-offset-background rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <Card
              className={cn(
                "border-border/60 hover:border-border h-full transition-colors",
                "hover:bg-muted/40",
              )}
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <span className="bg-primary/10 text-primary grid size-9 place-items-center rounded-md">
                    <card.icon className="size-4" />
                  </span>
                  <CardTitle className="text-base">{card.title}</CardTitle>
                </div>
                <CardDescription className="mt-2 leading-snug">{card.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex items-center justify-between pt-0">
                {card.count !== null ? (
                  <span className="text-muted-foreground text-sm tabular-nums">
                    <span className="text-foreground font-semibold">
                      {card.count.toLocaleString()}
                    </span>{" "}
                    {card.countLabel}
                  </span>
                ) : isLoading ? (
                  <span className="text-muted-foreground text-xs">Loading…</span>
                ) : (
                  <span className="text-muted-foreground text-xs">View matrix</span>
                )}
                <ArrowRight aria-hidden="true" className="text-muted-foreground size-4" />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {!isLoading && (subjects.length === 0 || sections.length === 0) ? (
        <Card className="mt-6 border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Sparkles aria-hidden="true" className="size-4 text-amber-700 dark:text-amber-300" />
              <CardTitle className="text-base text-amber-900 dark:text-amber-100">
                Catalogue isn't fully configured yet
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-amber-900/80 dark:text-amber-100/80">
            <ul className="ml-4 list-disc space-y-1">
              {subjects.length === 0 ? (
                <li>
                  No subjects configured — the timetable solver and eligibility builder both need at
                  least one.
                </li>
              ) : null}
              {sections.length === 0 ? (
                <li>
                  No sections configured — every timetable slot and roll-call row pivots off a
                  section.
                </li>
              ) : null}
            </ul>
            <p className="text-xs">
              Once both lists are populated, head to the Staff invites flow to assign teachers to
              the (subject, grade, section) combos they're qualified for.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {isReadyMatrix ? (
        <Card className="mt-6">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">Quick stats</CardTitle>
              <Badge variant="outline" className="font-normal">
                Live
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat label="Total subjects" value={subjects.length} />
            <Stat label="Total sections" value={sections.length} />
            <Stat
              label="Subjects with grade scope"
              value={subjects.filter((s) => s.applicable_grades.length > 0).length}
            />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-foreground text-2xl font-semibold tabular-nums">
        {value.toLocaleString()}
      </span>
    </div>
  );
}
